import { Controller } from "@hotwired/stimulus"
import { formatText, loadPreferences } from '../utils/text_formatter'
import { preloadOpenCC } from '../utils/text_formatter/opencc_loader'

/**
 * Notion 表單提交控制器。
 *
 * 處理表單的 AJAX 提交，顯示載入狀態和結果訊息。
 * 成功時清空「公司」和「內容」欄位，保留「Source」方便連續輸入。
 * 支援「套用格式整理器」按鈕，一鍵格式化內容欄位。
 */
export default class extends Controller {
  static targets = [
    'form',
    'source',
    'company',
    'status',
    'entryDate',
    'content',
    'submitButton',
    'buttonText',
    'spinner',
    'statusText',
    'message'
  ]

  /**
   * Controller 連接時初始化。
   *
   * 設定日期欄位預設值為今天。
   */
  connect() {
    this.setDefaultDate()
    // 背景載入 opencc-js（簡繁轉換），供 applyFormatter() 使用
    preloadOpenCC().catch(() => {})
  }

  /**
   * 設定日期欄位預設值為今天。
   *
   * 使用本地時區的日期格式 YYYY-MM-DD（HTML5 date input 要求格式）。
   */
  setDefaultDate() {
    if (this.hasEntryDateTarget && !this.entryDateTarget.value) {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      this.entryDateTarget.value = `${year}-${month}-${day}`
    }
  }

  /**
   * 點擊日期欄位時打開 date picker。
   *
   * 使用 HTML5 showPicker() API，讓點擊整個欄位都能觸發 date picker，
   * 而不只是點擊右側的小 icon。
   */
  openDatePicker() {
    if (this.hasEntryDateTarget && this.entryDateTarget.showPicker) {
      this.entryDateTarget.showPicker()
    }
  }

  /**
   * 套用格式整理器到內容欄位。
   *
   * 讀取使用者在格式調整器頁面設定的偏好（localStorage），
   * 將內容欄位的文字原地格式化。使用者可在送出前檢查結果。
   */
  applyFormatter() {
    const content = this.contentTarget.value
    if (!content.trim()) return

    const enabledIds = loadPreferences()
    this.contentTarget.value = formatText(content, enabledIds)
  }

  /**
   * 處理表單提交。
   *
   * 使用 fetch API 發送 AJAX 請求，不重新整理頁面。
   * @param {Event} event - 表單提交事件
   */
  async submit(event) {
    event.preventDefault()

    // 驗證必填欄位
    if (!this.validateForm()) {
      return
    }

    this.showLoading()
    this.hideMessage()

    try {
      const response = await fetch('/notion_entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          source: this.sourceTarget.value.trim(),
          company: this.companyTarget.value.trim(),
          status: this.hasStatusTarget ? this.statusTarget.value : null,
          entry_date: this.hasEntryDateTarget ? this.entryDateTarget.value : null,
          content: this.contentTarget.value
        })
      })

      const data = await response.json()

      if (data.success) {
        this.showSuccess(data.message, data.page_url)
        this._saveSourceHistory()
        this.clearForm()
      } else {
        this.showError(data.message)
      }
    } catch (error) {
      console.error('Notion form submission error:', error)
      this.showError('網路錯誤，請檢查網路連線後再試')
    } finally {
      this.hideLoading()
    }
  }

  /**
   * 驗證表單必填欄位。
   *
   * @returns {boolean} 驗證是否通過
   */
  validateForm() {
    const source = this.sourceTarget.value.trim()
    const company = this.companyTarget.value.trim()
    const content = this.contentTarget.value.trim()

    if (!source || !company || !content) {
      this.showError('請填寫所有必填欄位')
      return false
    }

    return true
  }

  /**
   * 取得 CSRF Token。
   *
   * @returns {string} CSRF Token 值
   */
  getCSRFToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag ? metaTag.content : ''
  }

  /**
   * 顯示載入狀態。
   */
  showLoading() {
    this.submitButtonTarget.disabled = true
    this.buttonTextTarget.textContent = '送出中...'
    this.spinnerTarget.classList.remove('hidden')
    this.statusTextTarget.textContent = '正在更新到 Notion...'
    this.statusTextTarget.classList.remove('hidden')
  }

  /**
   * 隱藏載入狀態。
   */
  hideLoading() {
    this.submitButtonTarget.disabled = false
    this.buttonTextTarget.textContent = '送出'
    this.spinnerTarget.classList.add('hidden')
    this.statusTextTarget.classList.add('hidden')
  }

  /**
   * 顯示成功訊息。
   *
   * @param {string} message - 成功訊息
   * @param {string} pageUrl - Notion 頁面 URL
   */
  showSuccess(message, pageUrl) {
    const messageEl = this.messageTarget

    // 設定成功樣式
    messageEl.className = 'mb-5 p-4 rounded-lg border transition-all duration-300 bg-teal-50 border-teal-200 text-teal-800'

    // 建構訊息內容
    let content = `<div class="flex items-start gap-3">
      <svg class="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <div>
        <p class="font-medium">${this.escapeHtml(message)}</p>`

    if (pageUrl) {
      content += `<a href="${this.escapeHtml(pageUrl)}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 mt-2 text-sm text-teal-700 hover:text-teal-900 underline underline-offset-2">
          開啟 Notion 頁面
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
          </svg>
        </a>`
    }

    content += `</div></div>`
    messageEl.innerHTML = content
    messageEl.classList.remove('hidden')

    // 滾動到訊息位置
    messageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  /**
   * 顯示錯誤訊息。
   *
   * @param {string} message - 錯誤訊息
   */
  showError(message) {
    const messageEl = this.messageTarget

    // 設定錯誤樣式
    messageEl.className = 'mb-5 p-4 rounded-lg border transition-all duration-300 bg-red-50 border-red-200 text-red-800'

    messageEl.innerHTML = `<div class="flex items-start gap-3">
      <svg class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <div>
        <p class="font-medium">${this.escapeHtml(message)}</p>
      </div>
    </div>`

    messageEl.classList.remove('hidden')

    // 滾動到訊息位置
    messageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  /**
   * 隱藏訊息區域。
   */
  hideMessage() {
    this.messageTarget.classList.add('hidden')
  }

  /**
   * 清空表單內容。
   *
   * 保留 source 欄位值（通常連續輸入時來源相同）。
   * 日期欄位重設為今天，方便連續輸入當天資料。
   */
  clearForm() {
    this.companyTarget.value = ''
    this.contentTarget.value = ''
    // source 保留，方便連續輸入相同來源的資料
    // 日期重設為今天，方便連續輸入當天資料
    this.setDefaultDate()
    // 將焦點移到公司欄位，方便繼續輸入
    this.companyTarget.focus()
  }

  /**
   * 跳脫 HTML 特殊字元，防止 XSS 攻擊。
   *
   * @param {string} text - 原始文字
   * @returns {string} 跳脫後的文字
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * 將目前的 source 值儲存至 localStorage 歷史記錄。
   *
   * 使用 MRU（Most Recently Used）排序，最新使用的在最前面。
   * 自動去重，上限 50 筆。Key 與 autocomplete controller 的 storageKey 一致。
   */
  _saveSourceHistory() {
    const STORAGE_KEY = 'notion-source-history'
    const MAX_ITEMS = 50
    const source = this.sourceTarget.value.trim()
    if (!source) return

    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      let history = raw ? JSON.parse(raw) : []

      // 去重：移除既有的同值項目
      history = history.filter(item => item !== source)
      // MRU：新值放最前面
      history.unshift(source)
      // 限制上限
      if (history.length > MAX_ITEMS) history = history.slice(0, MAX_ITEMS)

      localStorage.setItem(STORAGE_KEY, JSON.stringify(history))
    } catch (error) {
      console.warn('Failed to save source history:', error)
    }
  }
}
